import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/contexts/AuthContext";
import Header from "@/components/shared/Header";
import { Button } from "@/components/ui/button";
import PasswordInput from "@/components/shared/PasswordInput";
import { ArrowLeft, KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Profile page — self-service password change.
 *
 * Mounted at /app/profile. Available to all roles.
 */
export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirmNext, setConfirmNext] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const canSubmit =
    current.length >= 1 &&
    next.length >= 8 &&
    next === confirmNext &&
    next !== current;

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: current,
        new_password: next,
      });
      toast.success("Password updated. Please sign in again.");
      // Force re-login so the new password is used everywhere
      setTimeout(() => {
        logout();
        navigate("/login");
      }, 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-xl mx-auto px-5 sm:px-6 py-6">
        <button
          onClick={() => navigate(-1)}
          className="text-slate-500 hover:text-slate-800 inline-flex items-center text-[12.5px] mb-4"
          data-testid="profile-back-link"
        >
          <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back
        </button>

        <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-medium">
          Profile
        </p>
        <h1 className="text-2xl font-semibold text-slate-900 mb-1">
          Your account
        </h1>
        <p className="text-[13px] text-slate-500 mb-5">
          Signed in as <span className="font-medium text-slate-700">{user?.email}</span>
          {" · "}
          <span className="uppercase tracking-wider text-slate-400 text-[11px]">{user?.role}</span>
        </p>

        <section
          className="rounded-lg border border-slate-200 bg-white p-5 space-y-4"
          data-testid="profile-change-password-card"
        >
          <div className="flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-slate-500" />
            <h2 className="text-base font-semibold text-slate-900">Change password</h2>
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
              Current password
            </p>
            <PasswordInput
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              testId="profile-current-password"
            />
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
              New password
            </p>
            <PasswordInput
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              placeholder="Min 8 characters"
              testId="profile-new-password"
            />
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
              Confirm new password
            </p>
            <PasswordInput
              autoComplete="new-password"
              value={confirmNext}
              onChange={(e) => setConfirmNext(e.target.value)}
              testId="profile-confirm-password"
            />
            {confirmNext && next !== confirmNext && (
              <p className="text-[11.5px] text-red-600 mt-1">Passwords don't match.</p>
            )}
            {next && next === current && (
              <p className="text-[11.5px] text-amber-600 mt-1">
                New password must be different from current.
              </p>
            )}
          </div>

          <div className="flex justify-end pt-1">
            <Button
              onClick={submit}
              disabled={!canSubmit || submitting}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="profile-submit-btn"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <KeyRound className="w-4 h-4 mr-2" />
              )}
              Update password
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
}
